import React, { useRef, useEffect } from "react";
import { Box, IconButton, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import fav from "../assets/logo_icon.svg";
import searchService, { SearchResult } from "../services/searchService";
import { Place } from "@/types/common";
import Icons from "@/assets";
import PlaceAutocomplete from "@/components/home/PlaceAutoComplete";
import { FILTER_CATEGORIES, FilterCategory } from "@/utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import { setPlaceFilter } from "@/store/slices/placeFilterSlice";
import { FONT_SIZES } from "@/shared/constants/typography";

interface Position {
  coords: {
    latitude: number;
    longitude: number;
  };
}

interface HeaderProps {
  onFilterChange?: (filter: string) => void;
  onSearchResultSelect?: (result: Place) => void;
  handleInfoButtonClick?: () => void;
  userPosition?: Position;
}

// Component styles object
const componentStyles = {
  header: {
    p: { xs: "3%", sm: 2 },
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: { xs: "2%", sm: 1.5 },
    zIndex: 410,
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    background:
      "linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(255,255,255,0))",
  },
  logo: {
    width: { xs: "11.65vw", sm: 44 },
    height: { xs: "11.65vw", sm: 44 },
    cursor: "pointer",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "contain",
      boxShadow: "0px 1px 5px 0px #00000040",
      borderRadius: "50%",
    },
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    gap: "2%",
    flex: 1,
    position: "relative",
  },
  searchField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: "62px",
      fontSize: FONT_SIZES.SUBTITLE,
      color: "#626262",
      boxShadow: "0px 1px 5px 0px #00000040",
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "& input::placeholder": {
        color: "#626262",
        fontSize: FONT_SIZES.BODY,
        opacity: 1,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: { xs: "4.2% 2%", sm: "10px 14px" },
      color: "#626262",
    },
  },
  searchResults: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    borderRadius: "20px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    zIndex: 2000,
    maxHeight: "400px",
    overflowY: "auto",
    mt: "2%",
    width: "90.05%",
    mx: "auto",
    overscrollBehavior: "contain",
  },
  searchResultItem: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    py: "3%",
    px: "4%",
  },
  accessibilityDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    marginLeft: "2%",
    flexShrink: 0,
  },
  emptyState: {
    p: "5%",
    textAlign: "center",
    color: "#666",
  },
  loadingState: {
    p: "4%",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "2%",
  },
  errorState: {
    p: "6%",
    textAlign: "center",
    color: "#f44336",
  },
  filtersContainer: {
    p: { xs: "2%", sm: 1 },
    backgroundColor: "transparent",
    display: "flex",
    alignItems: "center",
    gap: { xs: "2%", sm: 1 },
    overflowX: "auto",
    "&::-webkit-scrollbar": { display: "none" },
    scrollbarWidth: "none",
  },
  filterButton: {
    color: "#201f1fff",
    borderColor: "#e0e0e0",
    borderRadius: "20px",
    fontSize: FONT_SIZES.TINY,
    fontWeight: "100",
    minWidth: "auto",
    px: { xs: "3%", sm: 2 },
    py: { xs: "1%", sm: 0.5 },
    whiteSpace: "nowrap",
    textTransform: "none",
    "&:hover": {
      borderColor: "#6b48ff",
      backgroundColor: "rgba(107, 72, 255, 0.04)",
    },
  },
  chip: {
    color: "#191919",
    borderColor: "#e0e0e0",
    fontSize: FONT_SIZES.CAPTION,
    borderRadius: "22px",
    height: { xs: "7.5vw", sm: 40 },
    px: "1.5%",
    boxShadow: "0px 1px 5px 0px #00000040",
    "& .MuiChip-icon": {
      color: "#6b48ff",
      fontSize: FONT_SIZES.BODY,
    },
    "&:hover": {
      borderColor: "#6b48ff",
      backgroundColor: "rgba(107, 72, 255, 0.04)",
    },
  },
  infoButton: {
    width: { xs: "11.65vw", sm: 48 },
    height: { xs: "11.65vw", sm: 48 },
    p: 0,
  },
};

const Header: React.FC<HeaderProps> = ({
  userPosition,
  onSearchResultSelect,
  handleInfoButtonClick,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const activeFilter = useSelector(
    (state: RootState) => state.placeFilter.selectedCategory,
  );

  useEffect(() => {
    return () => {
      searchService.cancelSearch();
    };
  }, []);

  const handleResultSelect = (result: SearchResult | Place, isOld?: boolean) => {
    const transformedPlace = isOld
      ? searchService.transformSearchResultToPlace(result as SearchResult)
      : (result as Place);

    onSearchResultSelect?.(transformedPlace);
    inputRef.current?.blur();
  };

  const handleFilterClick = (filter: FilterCategory) => {
    dispatch(setPlaceFilter(filter));
  };

  return (
    <>
      {/* Header */}
      <Box sx={componentStyles.header}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            gap: { xs: "2%", sm: 1.5 },
          }}
        >
          <Box
            sx={{ ...componentStyles.logo, cursor: "pointer" }}
            onClick={() => navigate("/dashboard")}
          >
            <img src={fav} alt="icon" />
          </Box>
          <Box sx={componentStyles.searchContainer}>
            <PlaceAutocomplete
              userLocation={userPosition}
              onSelect={(place: Place | null) => {
                if (place) handleResultSelect(place, false);
              }}
            />

            <Box>
              <IconButton
                sx={componentStyles.infoButton}
                onClick={handleInfoButtonClick}
                size="small"
              >
                <img
                  src={Icons.Info}
                  alt="info"
                  style={{ width: "100%", height: "100%" }}
                />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={componentStyles.filtersContainer}>
          {FILTER_CATEGORIES.map((filter) => {
            return (
              <Chip
                key={filter.label}
                label={filter.label}
                onClick={() => handleFilterClick(filter)}
                icon={
                  <Box
                    component="img"
                    src={filter.icon}
                    sx={{
                      height: { xs: "4.37vw", sm: 18 },
                      width: { xs: "4.37vw", sm: 18 },
                    }}
                  />
                }
                sx={{
                  ...componentStyles.chip,
                  background:
                    activeFilter?.label === filter.label
                      ? "#F0DEFF"
                      : "#ffffff",
                  borderColor:
                    activeFilter?.label === filter.label
                      ? "#F0DEFF"
                      : "#e0e0e0",
                  "&:hover": {
                    backgroundColor: "#F0DEFF",
                    borderColor: "#F0DEFF",
                  },
                }}
                variant="filled"
                clickable
              />
            );
          })}
        </Box>
      </Box>
    </>
  );
};

export default Header;
